import externalMetadataService from "./externalMetadataService";

export const CHAT_TYPE_PREFIX = "chat-session--";

const buildSessionKey = (username, personaId, topicKey, sessionId = null) => {
  return `${CHAT_TYPE_PREFIX}${username}--${personaId}--${topicKey}--${
    sessionId || ""
  }`;
};

const extractKeyParts = (key) => {
  const parts = key.split("--");
  if (parts.length < 4) {
    throw new Error("Invalid key format");
  }
  const username = parts[1];
  const personaId = parts[2];
  const topicId = parts[3];
  const sessionId = parts[4] || null;
  return { username, personaId, topicId, sessionId };
};

export const chatUiService = {
  /**
   * Get all chat sessions for a specific user and persona
   * @param {string} username - Current user's username
   * @param {string} personaId - ID of the persona
   * @param {string} topicKey - Key of the topic
   * @returns {Promise<Array>} - Array of session objects
   */
  getSessions: async (username, personaId, topicKey) => {
    try {
      const prefixKey = buildSessionKey(username, personaId, topicKey);
      const sessions = await externalMetadataService.getMetadataList(
        [prefixKey],
        null,
        false,
      );

      if (sessions && Object.keys(sessions).length > 0) {
        return Object.entries(sessions)
          .map(([key, value]) => ({
            id: key.replace(prefixKey, ""),
            title: value.title,
            date: value.date,
            messages: value.messages,
            deleted: value.deleted,
          }))
          .filter((session) => !session.deleted)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
      }
      return [];
    } catch (error) {
      console.error("Error fetching chat sessions:", error);
      return [];
    }
  },

  /**
   * Get a specific chat session by its ID
   * @param {string} username - Current user's username
   * @param {string} personaId - ID of the persona
   * @param {string} topicKey - ID of the topic
   * @param {string} sessionId - ID of the session
   * @returns {Promise<Object|null>} - Session object or null if not found
   */
  async getSession(username, personaId, topicKey, sessionId) {
    const key = `chat-session---${username}---${personaId}---${topicKey}---${sessionId}`;
    try {
      const sessionData = await localStorage.getItem(key);
      if (sessionData) {
        return JSON.parse(sessionData);
      }
      return null;
    } catch (error) {
      console.error("Error getting session:", error);
      return null;
    }
  },

  /**
   * Save or update a chat session
   * @param {string} username - Current user's username
   * @param {string} personaId - ID of the persona
   * @param {string} topicKey - Key of the topic
   * @param {string} sessionId - ID of the session
   * @param {Object} sessionData - Session data to save
   * @returns {Promise<boolean>} - Success status
   */
  saveSession: async (
    username,
    personaId,
    topicKey,
    sessionId,
    sessionData,
  ) => {
    try {
      const key = buildSessionKey(username, personaId, topicKey, sessionId);
      await externalMetadataService.upsertMetadata(key, sessionData);
      return true;
    } catch (error) {
      console.error("Error saving chat session:", error);
      return false;
    }
  },

  /**
   * Delete a chat session
   * @param {string} username - Current user's username
   * @param {string} personaId - ID of the persona
   * @param {string} topicId - ID of the topic
   * @param {string} sessionId - ID of the session to delete
   * @returns {Promise<boolean>} - Success status
   */
  deleteSession: async (username, personaId, topicId, sessionId) => {
    try {
      const key = buildSessionKey(username, personaId, topicId, sessionId);
      const obj = await externalMetadataService.getMetadata(key);
      if (!obj) return false;
      obj.deleted = true;
      await externalMetadataService.upsertMetadata(key, obj);
      return true;
    } catch (error) {
      console.error("Error deleting chat session:", error);
      return false;
    }
  },

  /**
   * Rename a chat session
   * @param {string} username - Current user's username
   * @param {string} personaId - ID of the persona
   * @param {string} topicKey - Key of the topic
   * @param {string} sessionId - ID of the session
   * @param {string} newTitle - New title for the session
   * @param {Object} sessionData - Current session data
   * @returns {Promise<boolean>} - Success status
   */
  renameSession: async (
    username,
    personaId,
    topicKey,
    sessionId,
    newTitle,
    sessionData,
  ) => {
    try {
      const updatedData = {
        ...sessionData,
        title: newTitle,
      };

      return await chatUiService.saveSession(
        username,
        personaId,
        topicKey,
        sessionId,
        updatedData,
      );
    } catch (error) {
      console.error("Error renaming chat session:", error);
      return false;
    }
  },

  getSessionHeaders: async (start, end) => {
    const records = await externalMetadataService.getMetadataList(
      [CHAT_TYPE_PREFIX],
      null,
      false,
      start,
      end,
      true,
    );
    return Object.entries(records).map(([key, value]) => ({
      id: key,
      name: value.title,
      created_by: extractKeyParts(key).username,
      created_at: value.__created_at,
      modality: "chat",
      assignment_id: "", // required, but not used in chat-ui
      direction: "inbound",
      from: value.personaName || "",
    }));
  },

  getSessionForSessionUI: async (sessionId) => {
    const records = await externalMetadataService.getMetadataList(
      [sessionId],
      null,
      true,
      null,
      null,
      true,
    );
    if (!records || Object.values(records).length === 0) return null;
    const record = Object.values(records)[0];
    return {
      id: sessionId,
      name: `${record.title} [${record.topicName} with ${record.personaName}]`,
      description: "This is a conversation within Chat UI",
      events: (record?.messages || []).map((m) => {
        return {
          stage: "primary",
          actor: m.role,
          event: m.role === "system" ? "system-prompt" : "message",
          data: {
            value: m.content,
          },
          timestamp: m.timestamp,
        };
      }),
      created_at: record.__created_at,
      created_by: record.__created_by,
      updated_at: record.__updated_at,
      updated_by: record.__updated_by,
      modality: "chat",
      persona: record.personaName,
      topic: record.topicName,
      variables: record.variables,
      models: { generative: record.generativeModel },
      tools: record.tools,
      deleted: record.deleted,
    };
  },
};

export default chatUiService;
